import { getTopicProblem } from "./topic-problems";
import type { TopicDefinition, TopicLocale } from "./topic-taxonomy";

export type TopicBaselineGuide = {
  title: string;
  statusLabel: string;
  summary: string;
  firstAction: string;
  steps: readonly { title: string; description: string }[];
  notice: string;
};

export function getTopicBaselineGuide(
  topic: TopicDefinition,
  locale: TopicLocale,
): TopicBaselineGuide {
  const problem = getTopicProblem(topic.id);
  const isKorean = locale === "ko";
  const situation = problem?.situation[locale];
  const firstAction =
    problem?.firstAction[locale] ??
    (isKorean
      ? "현재 상황과 받은 문서, 중요한 날짜를 먼저 정리하세요."
      : "Start by organising your situation, documents and important dates.");

  return {
    title: isKorean ? "지금 시작할 수 있는 기본 안내" : "A safe place to start",
    statusLabel: isKorean ? "상세 공식 가이드 준비 중" : "Detailed guide in preparation",
    summary:
      situation ??
      (isKorean
        ? `${topic.label}와 관련해 현재 상황과 필요한 행동을 먼저 구분하세요.`
        : `Start by separating your current situation from the action you need for ${topic.labelEn}.`),
    firstAction,
    steps: isKorean
      ? [
          {
            title: "1. 현재 상황을 기록하세요",
            description:
              "받은 문서와 메시지, 날짜, 금액, 접수번호가 있다면 한곳에 보관하세요.",
          },
          {
            title: "2. 공식 기관에서 현재 기준을 확인하세요",
            description:
              "자격, 기한, 비용과 결과를 추정하지 말고 해당 정부기관의 최신 안내를 확인하세요.",
          },
          {
            title: "3. 필요한 경우 전문 도움을 받으세요",
            description:
              "기한이 임박했거나 안전·건강·법률·세금·비자·큰 금액이 관련되면 등록 전문가나 공식 지원기관에 확인하세요.",
          },
        ]
      : [
          {
            title: "1. Record your current situation",
            description:
              "Keep notices, messages, dates, amounts and reference numbers together.",
          },
          {
            title: "2. Check the current official rules",
            description:
              "Do not assume eligibility, deadlines, fees or outcomes. Confirm them with the responsible government authority.",
          },
          {
            title: "3. Get specialist help when needed",
            description:
              "Seek registered or official help when a deadline, safety, health, legal, tax, visa or significant financial issue is involved.",
          },
        ],
    notice: isKorean
      ? "이 기본 안내는 확인된 상세 가이드를 대신하지 않습니다. 공식 출처 검토가 끝나는 대로 이 화면에 상세 단계와 출처를 연결합니다."
      : "This starting guide does not replace verified detailed guidance. Detailed steps and official sources will appear here after review.",
  };
}
